import React, { useContext, useEffect } from "react";

import { useVolatileCallback } from "@examspark/common-ui";
import { buildTagsSearchFilterFromContentFilter } from "@examspark/common-util";
import {
  AnyObject,
  Resource,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { getSearchQueryForField, useSearch } from "../../../../hooks/useSearch";
import {
  getObjectiveAuthorityIdsFromAppState,
  useAppContext,
} from "../../../../providers/AppContext";
import BrowserContextProvider, {
  BrowserContext,
} from "../../../../providers/BrowserContext";
import SearchContext, {
  SearchFields,
} from "../../../../providers/SearchContext";
import { safeLocalStorage } from "../../../../safeStorage";
import { ActivityEditorConfigContext } from "../ActivityEditor";

import { ItemPickerFilters } from "./ItemPickerFilters";
import ItemPickerLayout from "./ItemPickerLayout";
import ItemPickerMainLayout from "./ItemPickerMainLayout";
import ItemPickerPreview from "./ItemPickerPreview";

type ItemPickerProps = {
  onSelect: (itemIds: string[]) => void;
  selected?: Resource[];
  /** used in cases like swap item action that presents users with item picker
   * pre-selected with filters that matched the item user wants to swap */
  initialFilter?: SearchFields;
};

const ItemPickerInner = (props: ItemPickerProps) => {
  const [browserState, dispatchBrowser] = useContext(BrowserContext);

  const onSelect = useVolatileCallback(props.onSelect);
  useEffect(() => {
    onSelect(browserState.selected.map((item) => item.id));
  }, [browserState.selected, onSelect]);

  useEffect(() => {
    dispatchBrowser({
      type: "SET_SELECTED",
      data: props.selected ?? [],
      readOnly: true,
    });
  }, [dispatchBrowser, props.selected]);

  return (
    <ItemPickerLayout
      main={<ItemPickerMainLayout filters={<ItemPickerFilters />} />}
      preview={
        <ItemPickerPreview
          itemId={browserState.focused}
          onClose={() => dispatchBrowser({ type: "CLEAR_FOCUSED" })}
        />
      }
    />
  );
};

const ItemPicker = (props: ItemPickerProps) => {
  const config = useContext(ActivityEditorConfigContext);
  const [appState] = useAppContext();
  const objectiveAuthorityIds = getObjectiveAuthorityIdsFromAppState(appState);
  const localStorageKey = "examspark:examBuilderItemPickerFilters";

  const baseFilter: SearchableResourceFilterInput = {
    sk: {
      eq: "item#v0",
    },
    and: [
      {
        interaction: {
          ne: "gridded",
        },
      },
    ],
  };

  if (!baseFilter.and) baseFilter.and = [];
  if (objectiveAuthorityIds?.length) {
    const baseOrFilter: SearchableResourceFilterInput = {
      or: [
        getSearchQueryForField("objectiveAuthority", objectiveAuthorityIds),
        {
          workspaceId: {
            eq: appState.user?.workspaceId ?? "__EMPTY__",
          },
          creatorId: {
            eq: appState.user?.id ?? "__EMPTY__",
          },
        },
      ],
    };
    if (appState.workspaceUser?.id) {
      baseOrFilter.or!.push({
        workspaceId: {
          eq: appState.workspaceUser.id,
        },
      });
    }
    baseFilter.and.push(baseOrFilter);
  }

  /**
   * Handle filtering content by tags provided by SDK caller:
   * 1. Serve content shared with workspace (ie: not current workspace ID), or
   * 2. Serve content in workspace, but must have appropriate tags.
   */

  if (config.contentFilter?.tags) {
    const contentFilter = buildTagsSearchFilterFromContentFilter(
      config.contentFilter,
    );

    baseFilter.and.push({
      or: [
        {
          workspaceId: {
            ne: appState.workspace?.id ?? "__EMPTY__",
          },
        },
        {
          workspaceId: {
            eq: appState.workspace?.id,
          },
          ...contentFilter,
        },
      ],
    });
  }

  if (config.itemPicker?.baseFilter != null) {
    baseFilter.and.push(config.itemPicker.baseFilter);
  }

  const initialState =
    safeLocalStorage.parseItem<{
      document?: string;
      sections?: string[];
      language?: string;
      subject?: string;
    }>(localStorageKey) ?? {};

  const initialFilter: SearchFields =
    props.initialFilter ??
    config.itemPicker?.initialFilter ??
    (initialState
      ? {
          language: initialState.language ? [initialState.language] : [],
          subject: initialState.subject ? [initialState.subject] : [],
        }
      : {});

  const search = useSearch({
    queryKey: "item-picker-results",
    baseFilter: baseFilter,
    initialFilter,
    customAggs: config.itemPicker?.filters?.customTags?.map((t) => ({
      name: `tag:${t.key}`,
      type: "terms",
    })),
  });

  useEffect(() => {
    // do not persist filters when initialFilter prop is provided because it is
    // a one-time use case
    if (props.initialFilter) return;

    const nextFilter: AnyObject = {};
    if (search.state.filter.language?.length) {
      nextFilter.language = search.state.filter.language[0];
    }
    if (search.state.filter.subject?.length) {
      nextFilter.subject = search.state.filter.subject[0];
    }

    safeLocalStorage.serializeItem(localStorageKey, nextFilter);
  }, [
    props.initialFilter,
    search.state.filter.language,
    search.state.filter.subject,
  ]);

  return (
    <SearchContext.Provider value={search}>
      <BrowserContextProvider>
        <ItemPickerInner {...props} />
      </BrowserContextProvider>
    </SearchContext.Provider>
  );
};

export default ItemPicker;
